/**
 * Custom React hooks
 * Performance optimized with memoization and stable references
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import {
  fetchRemoteVersion,
  getLocalBuildId,
  setLocalBuildId,
  setLastReloadTime,
  type VersionInfo,
} from './version';
import { broadcastMandatoryUpdate } from './updateChannel';

/**
 * Get the single portal root for modals, sheets, and toasts.
 * All overlays should portal here so stacking and scroll lock behave correctly.
 * Falls back to document.body if #portal-root is missing (e.g. in tests).
 */
export function getPortalRoot(): HTMLElement {
  return document.getElementById('portal-root') || document.body;
}

/**
 * Hook to detect user's reduced motion preference
 * Returns true if user prefers reduced motion
 */
export function useReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    // Check if window is available (SSR safety)
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    // Modern browsers
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return prefersReducedMotion;
}

/**
 * Hook to detect app updates and prompt users to refresh
 * Checks on mount, visibility change, periodically, AND via Firestore real-time updates
 * Also supports mandatory updates when min_supported_web_build_id is set
 */
export interface AppUpdateState {
  updateAvailable: boolean;
  mandatoryUpdate: boolean;
  remoteVersion: VersionInfo | null;
  checking: boolean;
  dismiss: () => void;
  refresh: () => void;
}

const POLL_INTERVAL = 10 * 60 * 1000; // 10 minutes

// Firestore document for real-time version signals
const VERSION_DOC_PATH = 'app_config/version';

export function useAppUpdateCheck(): AppUpdateState {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [mandatoryUpdate, setMandatoryUpdate] = useState(false);
  const [remoteVersion, setRemoteVersion] = useState<VersionInfo | null>(null);
  const [checking, setChecking] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const checkingRef = useRef(false);

  const checkForUpdate = useCallback(async () => {
    // Use ref to prevent concurrent checks without triggering callback recreation
    if (checkingRef.current) return;

    checkingRef.current = true;
    setChecking(true);
    try {
      const remote = await fetchRemoteVersion();
      if (!remote) {
        checkingRef.current = false;
        setChecking(false);
        return;
      }

      const localBuildId = getLocalBuildId();

      // First load - store the build ID
      if (!localBuildId) {
        setLocalBuildId(remote.buildId);
        checkingRef.current = false;
        setChecking(false);
        return;
      }

      // Compare build IDs
      if (localBuildId !== remote.buildId) {
        setRemoteVersion(remote);
        setUpdateAvailable(true);
      }
    } catch (error) {
      console.warn('Error checking for update:', error);
    } finally {
      checkingRef.current = false;
      setChecking(false);
    }
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
    setUpdateAvailable(false);
  }, []);

  const refresh = useCallback(() => {
    // Store the new build ID before reloading
    if (remoteVersion) {
      setLocalBuildId(remoteVersion.buildId);
    }
    setLastReloadTime();
    window.location.reload();
  }, [remoteVersion]);

  // Check on mount
  useEffect(() => {
    checkForUpdate();
  }, [checkForUpdate]);

  // Check when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !dismissed) {
        checkForUpdate();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdate, dismissed]);

  // Poll periodically while tab is visible
  useEffect(() => {
    const startPolling = () => {
      if (pollTimerRef.current) return;

      pollTimerRef.current = setInterval(() => {
        if (document.visibilityState === 'visible' && !dismissed) {
          checkForUpdate();
        }
      }, POLL_INTERVAL);
    };

    const stopPolling = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    if (document.visibilityState === 'visible') {
      startPolling();
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startPolling();
      } else {
        stopPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdate, dismissed]);

  // Listen to Firestore for real-time version updates (instant push)
  useEffect(() => {
    const versionDocRef = doc(db, VERSION_DOC_PATH);

    const unsubscribe = onSnapshot(
      versionDocRef,
      (snapshot) => {
        if (!snapshot.exists()) return;

        const data = snapshot.data();
        const latestBuildId = data?.latest_web_build_id;
        const minBuildId = data?.min_supported_web_build_id;
        const localBuildId = getLocalBuildId();

        if (!localBuildId || !latestBuildId) return;

        // Check for mandatory update (local version is too old)
        if (minBuildId && localBuildId < minBuildId) {
          setMandatoryUpdate(true);
          setUpdateAvailable(true);
          broadcastMandatoryUpdate(minBuildId);
          return;
        }

        // Check for optional update
        if (localBuildId !== latestBuildId) {
          setUpdateAvailable(true);
        }
      },
      (error) => {
        // Silently fail - Firestore might not be configured or accessible
        console.warn('[useAppUpdateCheck] Firestore listener error:', error);
      }
    );

    return () => unsubscribe();
  }, []);

  return {
    updateAvailable: updateAvailable && !dismissed,
    mandatoryUpdate,
    remoteVersion,
    checking,
    dismiss,
    refresh,
  };
}

/**
 * Hook to detect virtual keyboard visibility using Visual Viewport API
 * Returns keyboard height and whether keyboard is visible
 * Works on iOS Safari and Android Chrome
 */
export function useVisualViewport() {
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const initialViewportHeight = useRef<number | null>(null);

  useEffect(() => {
    // Check if Visual Viewport API is available
    if (typeof window === 'undefined' || !window.visualViewport) {
      return;
    }

    const viewport = window.visualViewport;

    // Store initial viewport height
    if (initialViewportHeight.current === null) {
      initialViewportHeight.current = viewport.height;
    }

    const handleResize = () => {
      if (!initialViewportHeight.current) return;

      // Calculate keyboard height as difference between initial and current viewport
      const currentHeight = viewport.height;
      const heightDiff = initialViewportHeight.current - currentHeight;

      // Consider keyboard visible if height difference is significant (> 100px)
      // This accounts for minor viewport changes from browser chrome
      const isVisible = heightDiff > 100;

      setKeyboardHeight(isVisible ? heightDiff : 0);
      setIsKeyboardVisible(isVisible);
    };

    // Listen to viewport resize events
    viewport.addEventListener('resize', handleResize);
    viewport.addEventListener('scroll', handleResize);

    // Initial check
    handleResize();

    return () => {
      viewport.removeEventListener('resize', handleResize);
      viewport.removeEventListener('scroll', handleResize);
    };
  }, []);

  return {
    keyboardHeight,
    isKeyboardVisible,
  };
}

/**
 * Hook for debounced values - useful for search inputs, auto-save
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Hook to sync --app-height CSS variable with actual viewport height
 * Critical for iOS Safari where 100vh doesn't account for dynamic address bar
 * Also handles orientation changes and resize events
 *
 * Usage: Call once in App.tsx or AppShell to keep CSS variable in sync
 */
export function useAppHeight(): void {
  useEffect(() => {
    // Skip on server
    if (typeof window === 'undefined') return;

    const updateAppHeight = () => {
      // Use visualViewport if available (more accurate on mobile)
      const height = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${height}px`);
    };

    // Initial update
    updateAppHeight();

    // Listen to resize and orientation changes
    window.addEventListener('resize', updateAppHeight);
    window.addEventListener('orientationchange', updateAppHeight);

    // Visual Viewport API provides more granular updates on mobile
    // Particularly when keyboard appears/disappears or address bar shows/hides
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateAppHeight);
      window.visualViewport.addEventListener('scroll', updateAppHeight);
    }

    return () => {
      window.removeEventListener('resize', updateAppHeight);
      window.removeEventListener('orientationchange', updateAppHeight);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateAppHeight);
        window.visualViewport.removeEventListener('scroll', updateAppHeight);
      }
    };
  }, []);
}

/**
 * Hook for standardized overlay behavior (modals, sheets, dialogs)
 * Provides: body scroll lock, ESC key handling, focus management
 *
 * Usage:
 * ```tsx
 * const { containerRef } = useOverlay({
 *   isOpen,
 *   onClose,
 *   trapFocus: true, // optional, default true
 *   closeOnEscape: true, // optional, default true
 * });
 * ```
 */
export interface UseOverlayOptions {
  isOpen: boolean;
  onClose: () => void;
  /** Whether to trap focus within the overlay (default: true) */
  trapFocus?: boolean;
  /** Whether to close on ESC key press (default: true) */
  closeOnEscape?: boolean;
  /** Whether to lock body scroll (default: true) */
  lockScroll?: boolean;
}

export interface UseOverlayReturn {
  /** Ref to attach to the overlay container for focus trap */
  containerRef: React.RefObject<HTMLDivElement>;
}

export function useOverlay({
  isOpen,
  onClose,
  trapFocus = true,
  closeOnEscape = true,
  lockScroll = true,
}: UseOverlayOptions): UseOverlayReturn {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Body scroll lock
  useEffect(() => {
    if (!lockScroll) return;

    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, lockScroll]);

  // ESC key handler
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape]);

  // Focus management and focus trap
  useEffect(() => {
    if (!isOpen || !containerRef.current || !trapFocus) return;

    // Store previously focused element
    previousActiveElement.current = document.activeElement as HTMLElement;

    // Get all focusable elements
    const getFocusableElements = () => {
      if (!containerRef.current) return [];
      return Array.from(
        containerRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
    };

    // Focus first element
    const focusableElements = getFocusableElements();
    const firstElement = focusableElements[0];
    const focusTimer = setTimeout(() => firstElement?.focus(), 100);

    // Focus trap handler
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const elements = getFocusableElements();
      if (elements.length === 0) return;

      const first = elements[0];
      const last = elements[elements.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus on close
      previousActiveElement.current?.focus?.();
    };
  }, [isOpen, trapFocus]);

  return { containerRef };
}
